// @ts-check
/**
 * @file usePushPrompt.js
 * @location src/hooks/usePushPrompt.js
 * @role Push 권한 프롬프트(진입 5초 후 1회) + iOS PWA 설치 안내 카드 상태·핸들러.
 *   dismiss 시각을 uiPrefs(pushPromptDismissedAt / installCardDismissedAt)에 영속.
 */
import { useState, useEffect } from 'react';
import { requestAndSubscribe, isIosNeedingInstall, isPushSupported } from '../integrations/push.js';
import { addPushSubscription } from '../integrations/supabase/api/push.js';

/**
 * @param {object} p
 * @param {{ pushPromptDismissedAt?: number, installCardDismissedAt?: number }} p.uiPrefs
 * @param {(type: string, patch?: object) => void} p.act
 * @param {(msg: string, kind?: string) => void} p.showToast
 * @returns {{ pushPromptOpen: boolean, onEnablePush: () => Promise<void>, onDismissPush: () => void, installCardShow: boolean, onDismissInstall: () => void }}
 */
export function usePushPrompt({ uiPrefs, act, showToast }) {
  const [pushPromptOpen, setPushPromptOpen] = useState(false);
  const [installCardShow, setInstallCardShow] = useState(false);

  useEffect(() => {
    // 진입 5초 후 1회 — default 권한 + 미dismissed 조건
    const id = setTimeout(() => {
      if (!isPushSupported()) return;
      if (typeof Notification === 'undefined' || Notification.permission !== 'default') return;
      if (uiPrefs?.pushPromptDismissedAt) return;
      setPushPromptOpen(true);
    }, 5000);
    return () => clearTimeout(id);
    // reason: mount-only 1-shot prompt — uiPrefs 변동마다 재평가 X.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const standalone = typeof window !== 'undefined'
      ? (window.matchMedia?.('(display-mode: standalone)').matches ?? false)
      : false;
    if (
      isIosNeedingInstall(typeof navigator !== 'undefined' ? navigator.userAgent : '', standalone) &&
      !uiPrefs?.installCardDismissedAt
    ) {
      setInstallCardShow(true);
    }
    // reason: mount-only 1-shot — navigator.userAgent 불변, uiPrefs 재평가 X.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEnablePush = async () => {
    setPushPromptOpen(false);
    try {
      await requestAndSubscribe({ addPushSubscription });
      showToast('알림-유효-했습니다', 'ok');
    } catch {
      showToast('알림-유효화-실패했습니다', 'err');
    }
  };
  const onDismissPush = () => {
    setPushPromptOpen(false);
    act('SET_UI_PREF', { key: 'pushPromptDismissedAt', value: Date.now() });
  };
  const onDismissInstall = () => {
    setInstallCardShow(false);
    act('SET_UI_PREF', { key: 'installCardDismissedAt', value: Date.now() });
  };

  return { pushPromptOpen, onEnablePush, onDismissPush, installCardShow, onDismissInstall };
}
